<script lang="ts">
    import {
        isRenderable,
        type Renderable,
        isSlippable,
        type RenderSlippable,
        slip,
        next,
    } from "@circleofa/acim-engine"
    import type { Navigable } from "@circleofa/acim-engine"
    import Icon from "@components/material/Icon.svelte"
    import { registerHotKeyTap } from "@utils/underhood/registerHotkey"
    import { onDestroy } from "svelte"
    import { acim } from "@utils/acim"
    import { goto } from "$app/navigation"
    import { reviewPromptIfReady } from "@utils/underhood/review-prompt"
    export let page: Navigable

    $: change(page)

    function change(page: Navigable) {
        const list = acim.pageList.flatList
        for (let index = 0; index < list.length; index++) {
            const nav = list[index]

            if (nav.page == page) {
                // Found page

                // Process next
                if (index + 1 in list) {
                    const target = list[index + 1].page
                    if (isSlippable(target) || isRenderable(target)) {
                        nextPage = target
                    } else {
                        nextPage = null
                    }
                } else {
                    nextPage = null
                }

                // Process previous
                let previousIndex = index - 1
                let previousRenderable: Renderable | null = null
                while (previousIndex in list) {
                    const previousPageLoop = list[previousIndex].page
                    if (isRenderable(previousPageLoop)) {
                        previousRenderable = previousPageLoop
                        break
                    }
                    previousIndex--
                }

                previousPage = previousRenderable

                return
            }
        }
    }

    let nextPage: Renderable | RenderSlippable | null
    const goNext = () => {
        if (!nextPage) return
        goto("/book/" + slip(nextPage).path)
    }
    let labelNext: string | null = null
    $: labelNext =
        (nextPage?.buttonLabelOverride ? nextPage.buttonLabelOverride : nextPage?.label) ?? null

    let previousPage: Renderable | null = null
    const goPrev = () => {
        if (!previousPage) return
        goto("/book/" + slip(previousPage).path)
    }
    let labelPrev: string | null
    $: labelPrev =
        (previousPage?.buttonLabelOverride
            ? previousPage.buttonLabelOverride
            : previousPage?.label) ?? null

    let destroyLeft = registerHotKeyTap("ArrowLeft", goPrev)
    let destroyRight = registerHotKeyTap("ArrowRight", goNext)
    onDestroy(() => {
        destroyLeft()
        destroyRight()
    })
</script>

<div class="next-buttons-container">
    <button
        class:hidden={!previousPage}
        class="previousButton"
        aria-label="Previous page"
        on:click={goPrev}
    >
        <div class="circleButton"><Icon icon="arrow" /></div>
    </button>

    <button
        class:hidden={!nextPage}
        class="nextButton"
        aria-label="Next page"
        on:click={() => {
            // Maybe we want to review ?
            reviewPromptIfReady()

            // Go next
            goNext()
        }}
    >
        <div class="labelContainer"><span>{labelNext}</span></div>
        <div class="circleButton"><Icon icon="arrow" /></div>
    </button>
</div>

<style>
    .next-buttons-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
        position: sticky;
        bottom: var(--reader-sticky-bottom-clearance, 1rem);
        z-index: 5;
        margin-top: 1em;
        gap: 1em;
        pointer-events: none;
    }
    .next-buttons-container button {
        max-width: calc(100% - 40px);
        display: flex;
        align-items: center;
        pointer-events: auto;
        white-space: nowrap;
        min-width: 2.7em;
        height: 2.7em;
        border: 1px solid var(--color-purple-100);
        border-radius: 0.75rem;
        background: var(--color-grey-0);
        box-shadow: none;
        color: var(--color-purple-700);
        cursor: pointer;
        font-family:
            Inter,
            ui-sans-serif,
            system-ui,
            -apple-system,
            BlinkMacSystemFont,
            "Segoe UI",
            sans-serif;
        font-size: 1rem;
        font-weight: 600;
        letter-spacing: 0;
        line-height: 1;
        padding-right: 0;
        padding-left: 0;
        overflow: hidden;
        position: relative;
        text-align: center;
        text-decoration: none;
        text-overflow: ellipsis;
        text-transform: none;
        transition:
            transform 0.16s ease,
            border-color 0.16s ease,
            background-color 0.16s ease,
            color 0.16s ease,
            opacity 0.16s ease,
            filter 0.16s ease;
    }
    .next-buttons-container button:hover {
        border-color: var(--color-purple-100);
        background: var(--color-purple-50);
    }
    .next-buttons-container button:active {
        transform: translateY(1px);
    }
    .next-buttons-container button:focus-visible {
        outline: 2px solid var(--color-gold-400);
        outline-offset: 2px;
    }
    .next-buttons-container button.hidden {
        visibility: hidden;
    }
    .next-buttons-container button .labelContainer {
        display: block;
        padding-right: 3em;
        padding-left: 18px;
        text-overflow: ellipsis;
        overflow: hidden;
    }
    @media (hover: hover) {
        .next-buttons-container button:hover .labelContainer {
            transition-delay: 0s;
            padding-right: 125em;
        }
        .next-buttons-container button .labelContainer {
            transition: padding-right 0.3s 1s ease-out;
            margin-left: -18px;
            padding-left: 36px;
            padding-right: 25em;
            margin-right: -122em;
        }
    }
    .next-buttons-container button .circleButton {
        position: absolute;
        color: var(--color-grey-0);
        right: 0;
        padding: 0.5em;
        height: 100%;
        background: var(--color-purple-700);
        border-radius: var(--radius-button);
    }
    .next-buttons-container button.nextButton:hover .circleButton {
        background: var(--color-purple-800);
    }
    .next-buttons-container button .circleButton :global(svg) {
        height: 100%;
        position: relative;
        left: 0.05em;
        transform: rotate(180deg);
    }
    .next-buttons-container button.previousButton .circleButton {
        background-color: inherit;
    }
    .next-buttons-container button.previousButton .circleButton :global(svg) {
        color: var(--color-purple-700);
        transform: none;
        left: 0;
    }
    :global(.darkmode) .next-buttons-container button {
        border-color: var(--color-grey-700);
        background: var(--color-grey-800);
        color: var(--color-purple-100);
    }
    :global(.darkmode) .next-buttons-container button:hover {
        border-color: var(--color-grey-600);
        background: var(--color-grey-700);
        color: var(--color-grey-0);
    }
    :global(.darkmode) .next-buttons-container button .circleButton {
        color: var(--color-grey-0);
        background: var(--color-purple-500);
    }
    :global(.darkmode) .next-buttons-container button.nextButton:hover .circleButton {
        background: var(--color-purple-400);
    }
    :global(.darkmode) .next-buttons-container button.previousButton .circleButton {
        background-color: inherit;
    }
    :global(.darkmode) .next-buttons-container button.previousButton .circleButton :global(svg) {
        color: var(--color-purple-100);
    }
</style>
